"use client";

import AbblToast from "@/components/ui/AbblToast";
import React, { createContext, useCallback, useContext, useState } from "react";

type ToastType = "success" | "error";
type ToastPosition = "top-right" | "top-left" | "bottom-right" | "bottom-left" | "top-center" | "bottom-center";

interface ToastOptions {
  title: string;
  message?: string;
  type?: ToastType;
  position?: ToastPosition;
  duration?: number;
}

interface ToastContextType {
  showToast: (options: ToastOptions) => void;
  hideToast: () => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

export default function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toast, setToast] = useState<{
    show: boolean;
    title: string;
    message?: string;
    type: ToastType;
    position: ToastPosition;
  }>({
    show: false,
    title: "",
    message: "",
    type: "success",
    position: "top-right",
  });

  const hideToast = useCallback(() => {
    setToast((prev) => ({ ...prev, show: false }));
  }, []);

  const showToast = useCallback(
    ({ title, message, type = "success", position = "top-right", duration = 5000 }: ToastOptions) => {
      setToast({
        show: true,
        title,
        message,
        type,
        position,
      });

      if (duration > 0) {
        setTimeout(() => {
          hideToast();
        }, duration);
      }
    },
    [hideToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <AbblToast
        show={toast.show}
        onClose={hideToast}
        title={toast.title}
        message={toast.message}
        type={toast.type}
        position={toast.position}
      />
    </ToastContext.Provider>
  );
}
